import classNames from "classnames";
import "./Paragraph.css";

type Props = {
    className?: string;
    children: React.ReactNode;
    disabled?: boolean;
};

type BaseParagraph = (className?: string) => (props: Props) => React.JSX.Element;

function BaseParagraph(baseClassName?: string) {
    return function Paragraph(props: Props) {
        const { className, children, disabled } = props;
        return (
            <p
                className={classNames(
                    "paragraph",
                    baseClassName,
                    {
                        "paragraph--disabled": disabled,
                    },
                    className,
                )}
            >
                {children}
            </p>
        );
    };
}

function Add(baseParagraph: BaseParagraph, className: string) {
    return (newClassName?: string) =>
        baseParagraph(classNames(className, newClassName));
}

function Curry(baseParagraph: BaseParagraph) {
    return (className?: string) => baseParagraph(className);
}

const Colors = (baseParagraph: BaseParagraph) =>
    Object.assign(baseParagraph(), {
        N10: baseParagraph("paragraph--n10"),
        N20: baseParagraph("paragraph--n20"),
        N50: baseParagraph("paragraph--n50"),
        N60: baseParagraph("paragraph--n60"),
    });

const Weights = (baseParagraph: BaseParagraph) =>
    Object.assign(baseParagraph(), {
        Normal: Colors(Add(baseParagraph, "paragraph--normal")),
        SemiBold: Colors(Add(baseParagraph, "paragraph--semibold")),
        Light: Colors(Add(baseParagraph, "paragraph--light")),

        ...Colors(Curry(baseParagraph)),
    });

const Sizes = (baseParagraph: BaseParagraph) =>
    Object.assign(baseParagraph(), {
        Large: Weights(Add(baseParagraph, "paragraph--large")),
        Medium: Weights(Add(baseParagraph, "paragraph--medium")),
        Small: Weights(Add(baseParagraph, "paragraph--small")),
        XSmall: Weights(Add(baseParagraph, "paragraph--xsmall")),
    });

const Paragraph = Sizes(BaseParagraph);

export default Paragraph;
